import {
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { debounce, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useRef, useState } from 'react';

export interface MsUserPickerProps {
  placeholder?: string;
  label: string;
  value?: MsUserInfo;
  onValueChange: (newUser: MsUserInfo | null) => void;
}

export interface MsUserInfo {
  id: string;
  displayName: string;
  mail: string;
}

const debouncedFetch = debounce(
  (filter: string, fetchUsers: (f: string) => Promise<void>) =>
    fetchUsers(filter),
  300,
);

function matchSelection(
  left: MsUserInfo | undefined,
  right: MsUserInfo | undefined,
) {
  if (!left?.mail || !right?.mail) {
    return false;
  }
  return left.mail.toLowerCase() === right.mail.toLowerCase();
}

function useFetchUsers() {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const [users, setUsers] = useState<MsUserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef<AbortController | null>();

  const fetchUsers = async (filter: string = '') => {
    try {
      setLoading(true);
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();

      const baseUrl = await discoveryApi.getBaseUrl('ms-graph-proxy');
      const url = new URL(`${baseUrl}/users`);

      if (filter) {
        url.searchParams.set('filter', filter);
      }
      url.searchParams.set('count', '20');

      const response = await fetch(url.toString(), {
        signal: controllerRef.current.signal,
      });

      if (!response.ok) {
        alertApi.post({
          severity: 'error',
          message: `Failed to fetch users: ${response.statusText}`,
        });
        setUsers([]);
        return;
      }

      const data: MsUserInfo[] = await response.json();
      setUsers(data.filter(it => !!it.mail));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      // eslint-disable-next-line no-console
      console.error('error', err);
      alertApi.post({
        severity: 'error',
        message: err instanceof Error ? err.message : 'Unknown error occurred',
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    loading,
    fetchUsers,
  };
}

export const MsUserPicker = (props: MsUserPickerProps) => {
  const { onValueChange, value, label, placeholder } = props;

  const { users, loading, fetchUsers } = useFetchUsers();

  useEffect(() => {
    void fetchUsers();
    // eslint-disable-next-line
  }, []);

  const doFilter = (filter: string) => {
    // Search when input changes
    if (filter !== (value?.displayName || '')) {
      void debouncedFetch(filter, fetchUsers);
    }
  };

  const selectedUser = value
    ? users.find(user => user.mail === value.mail) || value
    : null;

  return (
    <Autocomplete
      fullWidth
      value={selectedUser}
      loading={loading}
      getOptionLabel={option => option.displayName || ''}
      getOptionSelected={matchSelection}
      onChange={(_, newValue) => onValueChange(newValue)}
      onInputChange={(_, newInputValue) => doFilter(newInputValue)}
      options={users}
      renderInput={params => (
        <TextField
          {...params}
          label={label || 'MS User'}
          placeholder={placeholder}
          fullWidth
        />
      )}
      renderOption={option => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{option.displayName}</div>
          {option.mail && (
            <div style={{ fontSize: '0.8em', color: 'gray' }}>
              {option.mail}
            </div>
          )}
        </div>
      )}
      noOptionsText={loading ? 'Loading...' : 'No users found'}
    />
  );
};
